import React, { useState } from 'react'
import { MoreVertical, Download, X } from 'lucide-react'
import { Layer } from '@feltmaps/js-sdk'
import Typography from '../atoms/Typography'
import { useTenantStore } from 'src/enviroment/store/tenentStore'
import SupportDialog from './SupportDialog'
import { SubscriptionPlanType } from '../../types/subscription'
import { useChatStore } from '../../enviroment/store/chatStore'
import { useShallow } from 'zustand/react/shallow'
import { logLimitHit } from '../../enviroment/api/metrics'

interface LayerListProps {
  layers: Layer[]
  onExport: (layer: Layer) => void
}

const LayerList = ({ layers, onExport }: LayerListProps) => {
  const [isListVisible, setIsListVisible] = useState(false)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [isSupportOpen, setIsSupportOpen] = useState<boolean>(false)
  const plan = useTenantStore((state) => state.currentTenant.plan)
  const isFreePlan = plan === SubscriptionPlanType.FREE
  const { setIsExpanded, upsertMessageByType } = useChatStore(
    useShallow((s) => ({ setIsExpanded: s.setIsExpanded, upsertMessageByType: s.upsertMessageByType })),
  )

  const handleMenuClick = (layerId: string) => {
    setOpenMenuId(openMenuId === layerId ? null : layerId)
  }

  const handleExport = (layer: Layer) => {
    onExport(layer)
    setOpenMenuId(null)
    setIsListVisible(false)
  }

  const handleExportButtonClick = () => {
    if (isFreePlan) {
      logLimitHit('LAYER_EXPORT', { plan })
      upsertMessageByType('export-layers')
      setIsExpanded(true)
    } else {
      setIsListVisible(true)
    }
  }

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.layers-container')) {
        setOpenMenuId(null)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  return (
    <>
      <div className="relative">
        <button
          onClick={handleExportButtonClick}
          className="export-layer-button flex items-center gap-2 rounded-3xl bg-light-green-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-light-green-600"
        >
          <Download className="h-4 w-4" />
          Export Layer
          {isFreePlan && (
            <span className="ml-1 rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-light-green-500">
              Pro
            </span>
          )}
        </button>

        {isListVisible && (
          <div className="layers-container absolute right-0 top-full z-50 mt-2 h-[300px] min-w-[280px] rounded-[8px] p-2 backdrop-blur">
            <div className="flex h-full flex-col rounded-[8px] bg-gray-900 p-3">
              <div className="flex items-center justify-between border-b border-gray-700 px-3 py-2">
                <Typography variant="sm" className="font-medium text-light-green-50">
                  Select layer
                </Typography>
                <button
                  onClick={() => {
                    setIsListVisible(false)
                    setOpenMenuId(null)
                  }}
                  className="rounded-full p-1 hover:bg-gray-800"
                >
                  <X className="h-4 w-4 text-light-green-50" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {layers.map((layer, index) => (
                  <div
                    key={layer.id}
                    className="group flex items-center justify-between border-b border-gray-700 px-3 py-2 last:border-0"
                  >
                    <div className="flex-1 truncate pr-2">
                      <Typography variant="sm" className="font-medium text-light-green-50">
                        {layer.name}
                      </Typography>
                    </div>

                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleMenuClick(layer.id)
                        }}
                        className="rounded-full p-1 hover:bg-gray-800 focus:outline-none"
                      >
                        <MoreVertical className="h-4 w-4 text-light-green-50 group-hover:text-light-green-100" />
                      </button>

                      {openMenuId === layer.id && (
                        <div
                          className={`absolute ${
                            index === layers.length - 1 ? 'bottom-full mb-1' : 'top-full mt-1'
                          } right-0 z-50 w-32 rounded-md bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5`}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleExport(layer)
                            }}
                            className="flex w-full items-center px-3 py-1.5 text-sm text-light-green-50 hover:bg-gray-800"
                          >
                            <Download className="mr-2 h-4 w-4" />
                            Export
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <SupportDialog isOpen={isSupportOpen} onClose={() => setIsSupportOpen(false)} />
    </>
  )
}

export default LayerList
